import React from 'react';
import { MapPin, Lock, QrCode, Truck, ShoppingCart, Minus, Plus, ChevronDown } from 'lucide-react';
import { fmt, tierFor } from './promoCart';

const REASSURANCE = [
  [MapPin, 'Entreprise suisse (Peseux)'],
  [Lock, 'Paiement sécurisé par carte'],
  [QrCode, 'Facture QR'],
  [Truck, 'Livraison gratuite en Suisse'],
];

export const PromoHeader = ({ bannerText }) => (
  <header className="promo-card-in border-b border-white/[0.06] lg:flex lg:items-center lg:justify-between lg:max-w-7xl lg:mx-auto lg:px-6" data-testid="promo-page-header">
    {bannerText && (
      <p className="text-center lg:text-left text-[11px] sm:text-xs md:text-sm font-semibold tracking-[0.18em] sm:tracking-[0.28em] uppercase text-[#C9A84C] py-3 px-4 lg:px-0 border-b lg:border-0 border-white/[0.06]" data-testid="promo-banner-text">
        {bannerText}
      </p>
    )}
    <ul className="flex flex-wrap justify-center gap-x-5 gap-y-2 px-4 lg:px-0 py-3 text-[11px] sm:text-xs text-zinc-400" data-testid="promo-reassurance">
      {REASSURANCE.map(([Icon, label]) => (
        <li key={label} className="flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-[#C9A84C]" />{label}</li>
      ))}
    </ul>
  </header>
);

export const PromoFaq = ({ faq }) => {
  if (!faq?.length) return null;
  return (
    <section className="max-w-3xl mx-auto pt-6" data-testid="promo-faq">
      <h2 className="text-lg md:text-xl font-semibold text-[#F2EFE8] mb-4">Questions fréquentes</h2>
      <div className="divide-y divide-white/[0.07] border-y border-white/[0.07]">
        {faq.map((item, i) => (
          <details key={item.question} className="group py-1" data-testid={`promo-faq-item-${i}`}>
            <summary className="flex items-center justify-between gap-4 cursor-pointer list-none min-h-[48px] text-sm md:text-base text-[#F2EFE8]">
              {item.question}
              <ChevronDown className="w-4 h-4 text-[#C9A84C] shrink-0 transition-transform group-open:rotate-180" />
            </summary>
            <p className="pb-4 text-sm text-zinc-400 whitespace-pre-line">{item.answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
};

export const MobileBuyBar = ({ offer, cart, qty, setQty, onAdded, onEdit }) => {
  if (!offer) return null;
  const inCart = cart.qtyOf(offer.slug);
  const maxAdd = Math.max(0, (offer.stock || 0) - inCart);
  const addQty = Math.min(qty, Math.max(1, maxAdd));
  const tier = tierFor(offer, inCart > 0 ? inCart : addQty);
  const add = () => { cart.add(offer.slug, addQty, offer.stock); setQty(1); onAdded(offer, addQty, tier.price_ht); };
  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-[#0B0B0C]/95 backdrop-blur-xl border-t border-white/10 px-3 pt-2.5 pb-[max(10px,env(safe-area-inset-bottom))]" data-testid="promo-mobile-bar">
      <p className="text-[11px] text-zinc-400 truncate mb-1.5" data-testid="promo-mobile-bar-name">{offer.name}</p>
      {offer.sold_out ? (
        <div className="h-12 rounded-xl border border-red-500/40 text-red-300 flex items-center justify-center font-semibold" data-testid="promo-mobile-bar-sold-out">Épuisé</div>
      ) : inCart > 0 ? (
        <button onClick={onEdit} className="w-full h-12 rounded-xl bg-[#C9A84C] text-black font-semibold flex flex-col items-center justify-center leading-tight" data-testid="promo-mobile-bar-edit">
          <span className="flex items-center gap-1.5 text-sm"><ShoppingCart className="w-4 h-4" /> Déjà {inCart} au panier · Modifier la quantité</span>
          <span className="text-[11px] font-medium tabular-nums">CHF {fmt(tier.price_ht)} HT / pce</span>
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-xl border border-zinc-700 shrink-0">
            <button onClick={() => setQty(Math.max(1, addQty - 1))} aria-label="Moins" className="w-11 h-12 flex items-center justify-center text-zinc-300" data-testid="promo-mobile-bar-minus"><Minus className="w-4 h-4" /></button>
            <span className="w-8 text-center text-[#F2EFE8] font-semibold tabular-nums" data-testid="promo-mobile-bar-qty">{addQty}</span>
            <button onClick={() => setQty(Math.min(maxAdd || 1, addQty + 1))} aria-label="Plus" className="w-11 h-12 flex items-center justify-center text-zinc-300" data-testid="promo-mobile-bar-plus"><Plus className="w-4 h-4" /></button>
          </div>
          <button onClick={add} disabled={maxAdd < 1} className="flex-1 min-w-0 h-12 rounded-xl bg-[#C9A84C] text-black font-semibold flex flex-col items-center justify-center leading-tight active:scale-[0.98] transition-transform disabled:opacity-40" data-testid="promo-mobile-bar-add">
            <span className="flex items-center gap-1.5 text-sm"><ShoppingCart className="w-4 h-4" /> Ajouter au panier</span>
            <span className="text-[11px] font-medium tabular-nums" data-testid="promo-mobile-bar-price">CHF {fmt(tier.price_ht)} HT / pce</span>
          </button>
        </div>
      )}
    </div>
  );
};
